import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, Building2, Wrench, X, Loader2 } from 'lucide-react';

export default function LiveSearch({ 
  type = 'hotspots', // 'hotspots' or 'tradies'
  placeholder = 'Search businesses...',
  city = null,
  className = ''
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Use relative URL - works on any domain
        const endpoint = type === 'hotspots' 
          ? `/api/hotspots/autocomplete?q=${encodeURIComponent(query)}${city ? `&city=${city}` : ''}`
          : `/api/autocomplete?q=${encodeURIComponent(query)}`;
        
        console.log('LiveSearch fetching:', endpoint);
        const res = await fetch(endpoint);
        const data = await res.json();
        console.log('LiveSearch results:', data);
        setSuggestions(data.suggestions || []);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Search error:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, type, city]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          navigate(suggestions[selectedIndex].url);
          setShowDropdown(false);
          setQuery('');
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div 
          onClick={focusInput}
          className="absolute left-4 top-1/2 -translate-y-1/2 cursor-text"
          style={{zIndex: 10}}
        >
          <Search className="w-5 h-5 text-slate-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="w-full py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all bg-white shadow-sm text-slate-900"
          style={{paddingLeft: '48px', paddingRight: '48px'}}
          data-testid="live-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-12 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 animate-spin" />
        )}
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50 max-h-96 overflow-y-auto"
          data-testid="live-search-dropdown"
        >
          {suggestions.map((item, index) => (
            <Link
              key={item.id}
              to={item.url}
              onClick={() => {
                setShowDropdown(false);
                setQuery('');
              }}
              className={`flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-sky-50' : ''
              }`}
              data-testid={`search-result-${item.id}`}
            >
              {/* Image/Icon */}
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {item.image ? (
                  <img src={item.image} alt="" className="w-full h-full object-cover" />
                ) : type === 'hotspots' ? (
                  <Building2 className="w-6 h-6 text-slate-400" />
                ) : (
                  <Wrench className="w-6 h-6 text-slate-400" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{item.name}</div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">
                    {type === 'hotspots' ? item.category : item.service}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {type === 'hotspots' ? item.city : item.location}
                  </span>
                </div>
              </div>

              {/* Rating/Verified */}
              <div className="flex items-center gap-1 text-sm flex-shrink-0">
                {item.rating && (
                  <span className="flex items-center gap-1 text-amber-500">
                    <Star className="w-4 h-4 fill-current" />
                    {item.rating}
                  </span>
                )}
                {item.verified && (
                  <span className="text-green-600 text-xs font-medium">✓</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* No results */}
      {showDropdown && query.length >= 2 && suggestions.length === 0 && !loading && (
        <div 
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 p-4 text-center text-slate-500 z-50"
        >
          No results found for "{query}"
        </div>
      )}
    </div>
  );
}
